import React from 'react';
import DatePicker from 'react-datepicker';
import {ChevronLeft, ChevronRight, Clock3} from 'lucide-react';
import 'react-datepicker/dist/react-datepicker.css';

export interface ScheduleEvent {
    id: number;
    title: string;
    time: string;
    description: string;
    date: string;
}

export interface SchedulingCalendarProps {
    selectedDate: Date;
    onChangeSelectedDate: (date: Date) => void;
    events: ScheduleEvent[];
}

function formatDateKey(date: Date) {
    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
}

const SchedulingCalendar: React.FC<SchedulingCalendarProps> = ({selectedDate, onChangeSelectedDate, events}) => {
    const eventsByDate = React.useMemo(() => {
        const map = new Map<string, ScheduleEvent[]>();
        events.forEach((event) => {
            const key = event.date;
            const existing = map.get(key) || [];
            map.set(key, [...existing, event]);
        });
        return map;
    }, [events]);

    const selectedDateKey = formatDateKey(selectedDate);
    const selectedDateEvents = eventsByDate.get(selectedDateKey) || [];

    return (
        <div data-scheduler>
            <h2 className="mb-2 text-[27px] font-medium text-mld-text-primary">Scheduling</h2>
            <div className="rounded-2xl border border-mld-panel-border bg-mld-menu-background p-6 shadow-[0_20px_65px_rgba(29,59,72,0.08)]">
                <div className="provider-calendar">
                    <DatePicker
                        inline
                        selected={selectedDate}
                        onChange={(date) => date && onChangeSelectedDate(date)}
                        calendarStartDay={0}
                        weekDayClassName={() => 'provider-calendar__weekday'}
                        formatWeekDay={(nameOfDay) => nameOfDay.slice(0, 3)}
                        renderCustomHeader={({date, decreaseMonth, increaseMonth}) => (
                            <div className="mb-3 flex items-center justify-between px-2 text-mld-text-primary">
                                <button
                                    type="button"
                                    onClick={decreaseMonth}
                                    aria-label="Previous month"
                                    className="text-mld-text-primary hover:text-mld-primary">
                                    <ChevronLeft className="h-6 w-6" />
                                </button>
                                <span className="Heading05Bold">
                                    {date.toLocaleDateString('en-US', {
                                        month: 'long',
                                        year: 'numeric',
                                    })}
                                </span>
                                <button
                                    type="button"
                                    onClick={increaseMonth}
                                    aria-label="Next month"
                                    className="text-mld-text-primary hover:text-mld-primary">
                                    <ChevronRight className="h-6 w-6" />
                                </button>
                            </div>
                        )}
                        renderDayContents={(dayOfMonth, date) => {
                            const key = formatDateKey(date);
                            const isSelected = key === selectedDateKey;
                            const hasEvents = eventsByDate.has(key);
                            return (
                                <span
                                    className={`relative flex h-10 w-10 items-center justify-center rounded-full text-sm font-semibold ${
                                        isSelected ? 'bg-mld-primary text-white shadow-lg' : 'text-mld-text-primary'
                                    } ${!isSelected ? 'hover:bg-transparent' : ''}`}>
                                    {dayOfMonth}
                                    {hasEvents && (
                                        <span
                                            className={`absolute bottom-1 h-1.5 w-1.5 rounded-full ${
                                                isSelected ? 'bg-white' : 'bg-mld-primary'
                                            }`}
                                        />
                                    )}
                                </span>
                            );
                        }}
                    />
                </div>
                <div className="mt-6 space-y-4">
                    {selectedDateEvents.length === 0 ? (
                        <div className="rounded-2xl border border-[#e2ebe3] bg-white/95 p-4 text-center shadow-sm">
                            <p className="ParagraphNormalBold text-mld-text-primary">
                                No events scheduled for{' '}
                                {selectedDate.toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    month: 'long',
                                    day: 'numeric',
                                })}
                            </p>
                            <p className="ParagraphSmallRegular text-[#7c8d80]">
                                Select another date to view upcoming sessions.
                            </p>
                        </div>
                    ) : (
                        selectedDateEvents.map((event) => (
                            <div
                                key={event.id}
                                className="rounded-2xl border border-[#e2ebe3] bg-white/95 p-4 shadow-sm">
                                <div className="flex items-center gap-2 text-[#2d8473]">
                                    <Clock3 className="h-4 w-4" />
                                    <p className="ParagraphSmallBold">{event.time}</p>
                                </div>
                                <p className="ParagraphNormalBold text-[#233c40]">{event.title}</p>
                                <p className="ParagraphSmallRegular text-[#7c8d80]">{event.description}</p>
                            </div>
                        ))
                    )}
                </div>
                <button
                    type="button"
                    className="mt-5 w-full rounded-full border border-[#dfe8e2] bg-white py-3 text-sm font-semibold text-[#5d7269] hover:border-[#2d8473] hover:text-[#2d8473]"
                    onClick={() => onChangeSelectedDate(new Date())}>
                    Today
                </button>
            </div>
        </div>
    );
};

export default SchedulingCalendar;
